import { User } from '@supabase/supabase-js';
import { z, ZodError, ZodSchema } from 'zod';

import { createSupabaseBrowserClient } from '../supabase/client';

type UserType = 'admin' | 'user';

type SchemaType<T> = T extends ZodSchema ? z.infer<T> : undefined;

type QueryFunctionContext<T extends ZodSchema | undefined> = {
  user: User;
  supabase: ReturnType<typeof createSupabaseBrowserClient>;
  params: SchemaType<T>;
};

type QueryHandler<T extends ZodSchema | undefined, R> = (
  ctx: QueryFunctionContext<T>,
) => Promise<R>;

type QueryOptions<T extends ZodSchema | undefined> = {
  paramsSchema?: T;
  userType?: UserType | UserType[];
};

export function authQuery<T extends ZodSchema | undefined, R>(
  handler: QueryHandler<T, R>,
  options: QueryOptions<T> = {},
) {
  return async (params?: SchemaType<T>): Promise<R> => {
    const supabase = createSupabaseBrowserClient();
    let user = null;
    try {
      const { data } = await supabase.auth.getUser();
      user = data.user;
    } catch {
      throw new Error('Unauthorized');
    }
    if (!user) throw new Error('Unauthorized');

    if (typeof options.userType === 'string') {
      if (user.user_metadata.role !== options.userType) {
        throw new Error(
          `You must be a ${options.userType} to access this data`,
        );
      }
    }
    if (Array.isArray(options.userType)) {
      if (!options.userType.includes(user.user_metadata.role)) {
        throw new Error(
          `You must be a ${options.userType.join(' or ')} to access this data`,
        );
      }
    }

    if (options.paramsSchema) {
      try {
        params = options.paramsSchema.parse(params);
      } catch (error) {
        if (error instanceof ZodError) {
          throw new Error('Invalid parameters');
        }
        throw error;
      }
    }

    return handler({ user, supabase, params: params as SchemaType<T> });
  };
}
